import React, { useEffect, useRef, useState } from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronLeft, Compose, Refresh, Stop, Warning } from "@gaming/xds-react-js-icons";

import { supportAssistantStore, useSupportAssistant } from "../../helpers/supportAssistantStore";
import { useStrings } from "../../localization/localizationHelpers";
import { AssistantErrorCode, assistantErrorCodes } from "../../models/supportAssistant";
import { AssistantAnswer } from "./AssistantAnswer";
import { AssistantInput } from "./AssistantInput";
import { ConfirmNewConversation } from "./ConfirmNewConversation";
import * as styles from "./SupportAssistant.module.css";

const errorKeys: Readonly<Record<AssistantErrorCode, string>> = {
  BAD_REQUEST: "supportAssistant_errorBadRequest",
  RATE_LIMITED: "supportAssistant_errorRateLimited",
  MODEL_UNAVAILABLE: "supportAssistant_errorModelUnavailable",
  TIMEOUT: "supportAssistant_errorTimeout",
  NO_HUB_PROFILE: "supportAssistant_errorNoHubProfile",
  UNAVAILABLE: "supportAssistant_errorUnavailable",
};

/** Full chat view inside the flyout (FR-029b/c). */
export const AssistantChat: React.FC = () => {
  const { state, context } = useSupportAssistant();
  const [isConfirming, setIsConfirming] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const [back, newConversation, chatLabel, searching, cancel, retry, suggestionsHeading] =
    useStrings(
      "supportAssistant_back",
      "supportAssistant_newConversation",
      "supportAssistant_chatLabel",
      "supportAssistant_searching",
      "supportAssistant_cancel",
      "supportAssistant_retry",
      "supportAssistant_suggestionsHeading"
    );
  const errorMessages = useStrings(...assistantErrorCodes.map(code => errorKeys[code]));

  const lastMessage = state.messages[state.messages.length - 1];
  const followUps = state.pending ? [] : lastMessage?.answer?.suggestedFollowUps ?? [];

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [state.messages.length, state.pending]);

  return (
    <div className={styles.chat} data-testid="support-assistant-chat">
      <div className={styles.chatHeader}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={back}
          title={back}
          onClick={() => supportAssistantStore.setView("home")}
          data-testid="support-assistant-back"
        >
          <Icon aria-hidden="true" icon={ChevronLeft} iconSize={IconSize.Custom} />
        </button>
        <h3 className={styles.chatTitle}>{state.title}</h3>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={newConversation}
          title={newConversation}
          onClick={() => setIsConfirming(true)}
        >
          <Icon aria-hidden="true" icon={Compose} iconSize={IconSize.Custom} />
        </button>
      </div>
      {isConfirming && (
        <ConfirmNewConversation
          onConfirm={() => {
            setIsConfirming(false);
            supportAssistantStore.clear();
          }}
          onCancel={() => setIsConfirming(false)}
        />
      )}
      <div className={styles.messages} role="log" aria-live="polite" aria-label={chatLabel}>
        {state.messages.map((message, index) =>
          message.role === "user" ? (
            <p key={index} className={styles.userMessage}>
              {message.text}
            </p>
          ) : message.answer ? (
            <AssistantAnswer key={index} answer={message.answer} />
          ) : (
            <div key={index} className={styles.notice} data-testid="support-assistant-error">
              <Icon aria-hidden="true" icon={Warning} iconSize={IconSize.Custom} />
              <span>
                {errorMessages[assistantErrorCodes.indexOf(message.error ?? "UNAVAILABLE")]}
              </span>
              {index === state.messages.length - 1 && (
                <button
                  type="button"
                  className={styles.textButton}
                  onClick={() => supportAssistantStore.retry(context)}
                >
                  <Icon aria-hidden="true" icon={Refresh} iconSize={IconSize.Custom} /> {retry}
                </button>
              )}
            </div>
          )
        )}
        {state.pending && (
          <div className={styles.pending} data-testid="support-assistant-pending">
            <span>{searching}</span>
            <button
              type="button"
              className={styles.textButton}
              onClick={supportAssistantStore.cancel}
            >
              <Icon aria-hidden="true" icon={Stop} iconSize={IconSize.Custom} /> {cancel}
            </button>
          </div>
        )}
        {followUps.length > 0 && (
          <div>
            <h4 className={styles.subheading}>{suggestionsHeading}</h4>
            <ul className={styles.chips}>
              {followUps.map(followUp => (
                <li key={followUp}>
                  <button
                    type="button"
                    className={styles.chip}
                    onClick={() => supportAssistantStore.send(followUp, context)}
                  >
                    {followUp}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <AssistantInput focusOnMount />
    </div>
  );
};
